import * as THREE from 'three';
import { TerrainFeature } from './TerrainFeature';
import { CraterBowlConfig, TerrainBounds, TERRAIN_COLORS } from './TerrainConfig';

/**
 * Crater Bowl - Speed bowls with elevated rims for launching.
 * Rim height: 5-8m provides launch opportunities.
 * Central depression creates natural speed runs.
 */
export class CraterBowl extends TerrainFeature {
  private outerRadius: number;
  private innerRadius: number;
  private rimHeight: number;
  private depth: number;

  constructor(scene: THREE.Scene, config: CraterBowlConfig) {
    super(scene, config.position, config.rotation || 0, 'crater_bowl');

    this.outerRadius = config.outerRadius;
    this.innerRadius = config.innerRadius;
    this.rimHeight = config.rimHeight;
    this.depth = config.depth;

    this.createMesh();
    this.addToScene();
  }

  private createMesh(): void {
    const radialSegments = 32;
    const heightSegments = 16;
    const geometry = new THREE.BufferGeometry();
    const vertices: number[] = [];
    const indices: number[] = [];

    // Generate vertices in concentric rings
    for (let r = 0; r <= heightSegments; r++) {
      const radiusProgress = r / heightSegments;
      const currentRadius = radiusProgress * this.outerRadius;

      const height = this.calculateHeight(currentRadius);

      for (let t = 0; t <= radialSegments; t++) {
        const theta = (t / radialSegments) * Math.PI * 2;
        vertices.push(
          Math.cos(theta) * currentRadius,
          height,
          Math.sin(theta) * currentRadius
        );
      }
    }

    // Generate indices
    for (let r = 0; r < heightSegments; r++) {
      for (let t = 0; t < radialSegments; t++) {
        const a = r * (radialSegments + 1) + t;
        const b = a + 1;
        const c = a + (radialSegments + 1);
        const d = c + 1;

        indices.push(a, c, b);
        indices.push(b, c, d);
      }
    }

    geometry.setAttribute(
      'position',
      new THREE.Float32BufferAttribute(vertices, 3)
    );
    geometry.setIndex(indices);
    geometry.computeVertexNormals();

    // Main bowl mesh
    const material = this.createTerrainMaterial(TERRAIN_COLORS.BASE);
    const bowlMesh = new THREE.Mesh(geometry, material);
    this.mesh.add(bowlMesh);

    // Add rim highlight
    this.createRimHighlight();

    // Add center marker
    this.createCenterMarker();
  }

  private calculateHeight(radius: number): number {
    if (radius <= this.innerRadius) {
      // Flat center (slightly depressed)
      return -this.depth;
    }

    const rimPosition = this.outerRadius * 0.7; // Rim peak at 70% of radius

    if (radius <= rimPosition) {
      // Rising slope from center to rim
      const progress = (radius - this.innerRadius) / (rimPosition - this.innerRadius);
      const riseHeight = this.rimHeight + this.depth;
      return -this.depth + riseHeight * this.smoothstep(0, 1, progress);
    } else {
      // Falling slope from rim to edge
      const progress = (radius - rimPosition) / (this.outerRadius - rimPosition);
      return this.rimHeight * (1 - this.smoothstep(0, 1, progress));
    }
  }

  private createRimHighlight(): void {
    // Glowing ring at rim peak
    const rimPosition = this.outerRadius * 0.7;

    const ringGeometry = new THREE.TorusGeometry(rimPosition, 0.5, 8, 48);
    const ringMaterial = new THREE.MeshStandardMaterial({
      color: TERRAIN_COLORS.EDGE_HIGHLIGHT,
      emissive: TERRAIN_COLORS.EDGE_HIGHLIGHT,
      emissiveIntensity: 1.5,
      transparent: true,
      opacity: 0.7,
    });

    const ring = new THREE.Mesh(ringGeometry, ringMaterial);
    ring.position.y = this.rimHeight + 0.3;
    ring.rotation.x = Math.PI / 2;
    this.mesh.add(ring);

    // Inner ring at bowl edge
    const innerRingGeometry = new THREE.TorusGeometry(this.innerRadius, 0.3, 8, 32);
    const innerRingMaterial = new THREE.MeshStandardMaterial({
      color: TERRAIN_COLORS.GRID_LINE,
      emissive: TERRAIN_COLORS.GRID_LINE,
      emissiveIntensity: 1,
      transparent: true,
      opacity: 0.5,
    });

    const innerRing = new THREE.Mesh(innerRingGeometry, innerRingMaterial);
    innerRing.position.y = -this.depth + 0.2;
    innerRing.rotation.x = Math.PI / 2;
    this.mesh.add(innerRing);
  }

  private createCenterMarker(): void {
    // Central beacon at bottom of bowl
    const beaconGeometry = new THREE.ConeGeometry(1.5, 4, 8);
    const beaconMaterial = new THREE.MeshStandardMaterial({
      color: TERRAIN_COLORS.PEAK_GLOW,
      emissive: TERRAIN_COLORS.PEAK_GLOW,
      emissiveIntensity: 2,
      transparent: true,
      opacity: 0.8,
    });

    const beacon = new THREE.Mesh(beaconGeometry, beaconMaterial);
    beacon.position.y = -this.depth + 2;
    this.mesh.add(beacon);

    // Ground pattern at center
    const groundRingGeometry = new THREE.RingGeometry(0.5, 3, 32);
    const groundRingMaterial = new THREE.MeshStandardMaterial({
      color: TERRAIN_COLORS.PEAK_GLOW,
      emissive: TERRAIN_COLORS.PEAK_GLOW,
      emissiveIntensity: 0.5,
      transparent: true,
      opacity: 0.4,
      side: THREE.DoubleSide,
    });

    const groundRing = new THREE.Mesh(groundRingGeometry, groundRingMaterial);
    groundRing.position.y = -this.depth + 0.1;
    groundRing.rotation.x = -Math.PI / 2;
    this.mesh.add(groundRing);
  }

  getHeightAtPosition(worldPos: THREE.Vector3): number | null {
    const local = this.worldToLocal(worldPos);
    const distance = Math.sqrt(local.x * local.x + local.z * local.z);

    // Check if within outer radius
    if (distance > this.outerRadius * 1.1) {
      return null;
    }

    const height = this.calculateHeight(distance);

    // Only return positive heights (rim)
    // For the bowl depression, bikes should fall to ground level
    if (height <= 0) {
      return null; // Let bike use ground level
    }

    return height;
  }

  getBounds(): TerrainBounds {
    const extent = this.outerRadius * 1.1;
    return {
      minX: this.position.x - extent,
      maxX: this.position.x + extent,
      minZ: this.position.z - extent,
      maxZ: this.position.z + extent,
    };
  }
}
